import { DOCUMENT, effect, EffectRef, inject, Injectable, untracked } from '@angular/core';
import { DataLayerItemBase, GTMPageBlockTrackingModel, NavigationTrackingEventName } from '@libs/shared/models';
import { TrackingConsentService } from '@libs/shared/services';
import {
  AccountDataLayerItem,
  AccountTrackingLevels,
  CategoryNavigationDataLayerItem,
  DataLayerDeliveryWindowsCalendarItem,
  DataLayerDeliveryWindowsItem,
  DataLayerEventName,
  DataLayerItem,
  DataLayerPageInfoItem,
  DataLayerPayload,
  NavigationDataLayerItem,
  PageBlockClickDataLayerItem,
  PageBlockImpressionDataLayerItem,
  PageRenderCompleteDataLayerItem,
  ProductClickDataLayerItem,
  ProductImpressionDataLayerItem,
  ProductTrackingModel,
} from '@libs/shop/models';
import { getTrackingPagePath } from '../context';

const MAX_PENDING_ITEMS: number = 100;

// Declare gTM dataLayer array
declare global {
  interface Window {
    dataLayer: DataLayerItem[];
  }
}

@Injectable({
  providedIn: 'root',
})
export class TrackingGoogleService {
  public readonly document: Document = inject(DOCUMENT);
  private readonly trackingConsent: TrackingConsentService = inject(TrackingConsentService);
  private readonly pendingItems: DataLayerItem[] = [];

  private readonly flushPendingItemsEffect: EffectRef = effect(() => {
    if (this.trackingConsent.given()) {
      this.flushPendingItems();
    }
  });

  /**
   * Pushes a typed event. `name` is any event in the DataLayerItem union;
   * `payload` is that event's shape minus its `event` property, inferred from `name`.
   */
  public trackEvent<TName extends DataLayerEventName>(
    name: TName,
    payload: NoInfer<DataLayerPayload<TName>>,
  ): void {
    const event: DataLayerItemBase = { event: name, ...payload };

    this.pushDataLayer(event as DataLayerItem);
  }

  public trackNavigationEvent(
    event: NavigationTrackingEventName,
    navigationElement: string,
    navigationLink: string,
  ): void {
    const payload: NavigationDataLayerItem = {
      event,
      navigationElement,
      navigationLink,
    };

    this.pushDataLayer(payload);
  }

  public trackAccountEvent(
    accountLevel: AccountTrackingLevels,
    accountAction: string,
  ): void {
    const payload: AccountDataLayerItem = {
      event: 'accountInteraction',
      accountLevel,
      accountAction,
    };

    this.pushDataLayer(payload);
  }

  public trackPageBlockClickEvent(...items: GTMPageBlockTrackingModel[]): void {
    const payload: PageBlockClickDataLayerItem = {
      event: 'select_promotion',
      ecommerce: {
        items,
      },
    };

    this.pushDataLayer(payload);
  }

  public trackPageBlockImpressionEvent(...items: GTMPageBlockTrackingModel[]): void {
    const payload: PageBlockImpressionDataLayerItem = {
      event: 'view_promotion',
      ecommerce: {
        items,
      },
    };

    this.pushDataLayer(payload);
  }

  public trackProductImpressionEvent(
    impressionProducts: ProductTrackingModel[],
    searchTerm: string | null = null,
  ): void {
    const payload: ProductImpressionDataLayerItem = {
      event: 'productsImpression',
      impressionProducts,
      searchTerm,
    };

    this.pushDataLayer(payload);
  }

  public trackPageInfoEvent(dataLayerPageInfoItem: DataLayerPageInfoItem): void {
    this.pushDataLayer(dataLayerPageInfoItem);
  }

  /** Dropped instead of buffered without consent, as GTM takes the push time as the render time. */
  public trackPageRenderCompleteEvent(): void {
    if (!untracked(this.trackingConsent.given)) {
      return;
    }

    const payload: PageRenderCompleteDataLayerItem = {
      event: 'pageRenderComplete',
      location: getTrackingPagePath(this.document),
    };

    this.pushDataLayer(payload);
  }

  public trackDeliveryWindowsDialogOpen(): void {
    const payload: DataLayerDeliveryWindowsItem = {
      event: 'deliveryDate',
      deliveryDateInteraction: 'open-info',
      deliveryDateLocation: getTrackingPagePath(this.document),
    };

    this.pushDataLayer(payload);
  }

  public trackSidebarNavigationEvent(navCategory: string): void {
    const payload: CategoryNavigationDataLayerItem = {
      event: 'sidebarNav',
      navCategory: navCategory,
      location: getTrackingPagePath(this.document),
    };

    this.pushDataLayer(payload);
  }

  public trackOpenDeliveryWindowsCalendarEvent(): void {
    const payload: DataLayerDeliveryWindowsCalendarItem = {
      event: 'deliveryDate',
      deliveryDateInteraction: 'open-calendar',
    };

    this.pushDataLayer(payload);
  }

  public trackChangeDateDeliveryWindowsCalendarEvent(deliveryDateDiff: number): void {
    const payload: DataLayerDeliveryWindowsCalendarItem = {
      event: 'deliveryDate',
      deliveryDateInteraction: 'changeDate',
      deliveryDateType: 'custom',
      deliveryDateDiff,
    };

    this.pushDataLayer(payload);
  }

  public trackProductClickEvent(products: ProductTrackingModel[], searchTerm: string | null = null): void {
    const payload: ProductClickDataLayerItem = {
      event: 'productClick',
      products,
      searchTerm,
    };

    this.pushDataLayer(payload);
  }

  public pushDataLayer(item: DataLayerItem): void {
    const trackingConsentGiven: boolean = untracked(this.trackingConsent.given);

    if (!trackingConsentGiven) {
      this.pendingItems.push(item);

      if (this.pendingItems.length > MAX_PENDING_ITEMS) {
        this.pendingItems.shift();
      }

      return;
    }

    this.flushPendingItems();
    this.getDataLayer().push(item);
  }

  private flushPendingItems(): void {
    if (this.pendingItems.length > 0) {
      this.getDataLayer().push(...this.pendingItems.splice(0));
    }
  }

  private getDataLayer(): DataLayerItem[] {
    const win: Window = this.document.defaultView ?? window;

    if (!Array.isArray(win.dataLayer)) {
      win.dataLayer = [];
    }

    return win.dataLayer;
  }
}
